import { useProductMappingChecksStore } from "./product-mapping-checks-store.js";
import { useProductBulkMonitoringStore } from "./product-bulk-monitoring-store.js";
import { useCatalogRepairStore } from "./catalog-repair-store.js";
import { useEligibilityResetStore } from "./eligibility-reset-store.js";
import { useProductBulkPolicyStore } from "../policies/product-bulk-policy-store.js";
import {
  buildConfirmedEligibilityUpdates,
  unconfirmedEligibilityResult,
} from "./eligibility-bulk-updates.js";
import { defineStore } from "pinia";
import type { LocationQuery, LocationQueryRaw } from "vue-router";

import { MerchantApiError } from "../authentication/errors.js";
import type {
  BulkEligibilityResult,
  BulkEligibilityUpdate,
  EligibilityDraft,
  MerchantJobStatusDto,
  ProductDetailResponse,
  ProductAttentionFilter,
  ProductFilters,
  ProductListFiltersViewModel,
  ProductListPageViewModel,
  ProductRow,
  ProductSectionStateViewModel,
  ProductSyncSummary,
} from "./models.js";
import { defaultProductListFilters, toProductFilters, type ProductsService } from "./service.js";
import { productSyncCatalogAction } from "./product-sync-rules.js";

export const useProductsStore = defineStore("merchant-products", {
  state: (): {
    activeJob?: MerchantJobStatusDto;
    bulkResult?: BulkEligibilityResult;
    bulkSubmitting: boolean;
    bulkNeedsRefresh: boolean;
    detail?: ProductDetailResponse;
    detailError?: string;
    drafts: Record<string, EligibilityDraft>;
    appliedFilters: ProductListFiltersViewModel;
    draftFilters: ProductListFiltersViewModel;
    filters: ProductListFiltersViewModel;
    listRequestId?: string;
    listError?: string;
    loadingDetail: boolean;
    loadingList: boolean;
    mutationMessage?: string;
    nextCursor: string | null;
    pageView: ProductListPageViewModel;
    products: ProductRow[];
    selectedIds: string[];
    service?: ProductsService;
    sync?: ProductSyncSummary;
    syncingCatalog: boolean;
  } => ({
    appliedFilters: defaultProductListFilters(),
    drafts: {},
    draftFilters: defaultProductListFilters(),
    filters: defaultProductListFilters(),
    bulkSubmitting: false,
    bulkNeedsRefresh: false,
    loadingDetail: false,
    loadingList: false,
    nextCursor: null,
    pageView: emptyProductListPageViewModel(),
    products: [],
    selectedIds: [],
    syncingCatalog: false,
  }),
  getters: {
    selectedProducts: (state): ProductRow[] =>
      state.products.filter((product) => state.selectedIds.includes(product.productId)),
    detailProduct: (state): ProductRow | undefined =>
      state.detail === undefined ? undefined : productRowFromDetail(state.detail),
    readOnlyDetail: (state): boolean => state.detail?.header.readOnly === true,
    syncCatalogAction: (state): { enabled: boolean; disabledReason?: string } =>
      productSyncCatalogAction({
        actions: state.pageView.actions,
        submitting: state.syncingCatalog,
      }),
  },
  actions: {
    configure(service: ProductsService): void {
      this.service = service;
    },
    async loadInitialFromRoute(routeQuery: LocationQuery): Promise<void> {
      const filters = this.requireService().filtersFromRouteQuery(routeQuery);
      this.draftFilters = { ...filters };
      this.appliedFilters = { ...filters };
      this.filters = { ...filters };
      await this.fetchAppliedList();
    },
    async loadList(filters: Partial<ProductListFiltersViewModel>): Promise<void> {
      const normalized = { ...defaultProductListFilters(), ...filters };
      this.draftFilters = { ...normalized };
      this.appliedFilters = { ...normalized };
      this.filters = { ...normalized };
      await this.fetchAppliedList();
    },
    async applyFilters(): Promise<LocationQueryRaw> {
      this.appliedFilters = withoutCursor(this.draftFilters);
      this.filters = { ...this.appliedFilters };
      this.selectedIds = [];
      this.clearBulkResult();
      await this.fetchAppliedList();
      return this.routeQuery();
    },
    async clearFilters(): Promise<LocationQueryRaw> {
      this.draftFilters = defaultProductListFilters();
      this.appliedFilters = defaultProductListFilters();
      this.filters = defaultProductListFilters();
      this.selectedIds = [];
      this.clearBulkResult();
      await this.fetchAppliedList();
      return this.routeQuery();
    },
    async loadNextPage(): Promise<void> {
      const nextCursor = this.pageView.pagination.nextCursor;
      if (nextCursor === null) return;
      await this.fetchNextPage(nextCursor);
    },
    async retry(): Promise<void> {
      await this.fetchAppliedList();
    },
    async clearCursorAndRetry(): Promise<LocationQueryRaw> {
      this.appliedFilters = withoutCursor(this.appliedFilters);
      this.filters = { ...this.appliedFilters };
      await this.fetchAppliedList();
      return this.routeQuery();
    },
    async reviewWarning(attention: Exclude<ProductAttentionFilter, "">): Promise<LocationQueryRaw> {
      const filters = {
        ...withoutCursor(this.appliedFilters),
        attention,
        sort: "NEEDS_ATTENTION_DESC" as const,
      };
      this.draftFilters = { ...filters };
      this.appliedFilters = { ...filters };
      this.filters = { ...filters };
      this.selectedIds = [];
      await this.fetchAppliedList();
      return this.routeQuery();
    },
    routeQuery(): LocationQueryRaw {
      return this.requireService().routeQueryFromFilters(this.appliedFilters);
    },
    updateDraftFilter<TKey extends keyof ProductListFiltersViewModel>(
      key: TKey,
      value: ProductListFiltersViewModel[TKey],
    ): void {
      this.draftFilters = { ...this.draftFilters, [key]: value };
    },
    async fetchAppliedList(): Promise<void> {
      this.loadingList = true;
      delete this.listError;
      delete this.listRequestId;
      try {
        this.pageView = await this.requireService().loadProducts(
          toProductFilters(this.appliedFilters),
          this.draftFilters,
          this.selectedIds.length,
        );
        this.nextCursor = this.pageView.pagination.nextCursor;
        this.products = this.pageView.rows.map(toCompatibilityProductRow);
        this.selectedIds = this.selectedIds.filter((id) =>
          this.pageView.rows.some((product) => product.productId === id),
        );
      } catch (error: unknown) {
        this.listError = messageForError(error, "Products could not be loaded.");
        if (error instanceof MerchantApiError) {
          this.listRequestId = error.requestId;
        }
        this.pageView = {
          ...this.pageView,
          sectionStates: [errorSection(error, this.listError)],
        };
      } finally {
        this.loadingList = false;
      }
    },
    async fetchNextPage(cursor: string): Promise<void> {
      this.loadingList = true;
      delete this.listError;
      delete this.listRequestId;
      try {
        const nextPage = await this.requireService().loadProducts(
          toProductFilters({ ...withoutCursor(this.appliedFilters), cursor }),
          this.draftFilters,
          this.selectedIds.length,
        );
        this.pageView = appendedProductPage(this.pageView, nextPage);
        this.nextCursor = this.pageView.pagination.nextCursor;
        this.products = this.pageView.rows.map(toCompatibilityProductRow);
        this.selectedIds = this.selectedIds.filter((id) =>
          this.pageView.rows.some((product) => product.productId === id),
        );
      } catch (error: unknown) {
        this.listError = messageForError(error, "More products could not be loaded.");
        if (error instanceof MerchantApiError) {
          this.listRequestId = error.requestId;
        }
        this.pageView = {
          ...this.pageView,
          sectionStates: [errorSection(error, this.listError)],
        };
      } finally {
        this.loadingList = false;
      }
    },
    async searchProductsForPolicyOverride(search: string): Promise<ProductRow[]> {
      const trimmed = search.trim();
      if (trimmed.length === 0) return [];

      const pageView = await this.requireService().loadProducts(
        toProductFilters({
          ...defaultProductListFilters(),
          search: trimmed,
          sort: "TITLE_ASC",
          limit: 8,
        }),
      );

      return pageView.rows.map(toCompatibilityProductRow);
    },
    async loadMore(): Promise<void> {
      await this.loadNextPage();
    },
    async loadDetail(productId: string): Promise<void> {
      const refreshesCurrentProduct = this.detail?.header.productId === productId;
      if (!refreshesCurrentProduct) {
        delete this.detail;
      }
      this.loadingDetail = true;
      delete this.detailError;
      try {
        this.detail = await this.requireService().loadProduct(productId);
        this.ensureDraft(productId, productRowFromDetail(this.detail));
      } catch (error: unknown) {
        this.detailError = messageForError(error, "Product detail could not be loaded.");
      } finally {
        this.loadingDetail = false;
      }
    },
    async fetchProductDetail(productId: string): Promise<ProductDetailResponse> {
      return this.requireService().loadProduct(productId);
    },
    toggleSelection(productId: string): void {
      this.selectedIds = this.selectedIds.includes(productId)
        ? this.selectedIds.filter((id) => id !== productId)
        : [...this.selectedIds, productId];
      this.clearBulkResult();
      this.updateBulkActionState();
    },
    clearSelection(): void {
      this.selectedIds = [];
      this.clearBulkResult();
      this.updateBulkActionState();
    },
    clearBulkResult(): void {
      if (this.bulkNeedsRefresh) return;
      delete this.bulkResult;
    },
    ensureDraft(productId: string, product: ProductRow): void {
      this.drafts[productId] ??= {
        eligible: product.eligible,
        reason:
          product.eligibilityReason ??
          (product.eligible ? "MERCHANT_ENABLED" : "MERCHANT_DISABLED"),
        merchantNote: product.eligibilityNote ?? "",
      };
    },
    async updateEligibility(productId: string): Promise<void> {
      const source =
        this.detail?.header.productId === productId && this.detail !== undefined
          ? productRowFromDetail(this.detail)
          : this.products.find((product) => product.productId === productId);
      if (source === undefined) return;
      delete this.detailError;
      try {
        const updated = await this.requireService().updateEligibility(
          productId,
          this.drafts[productId] ?? defaultEligibilityDraft(source),
        );
        this.applyUpdatedProduct(updated);
        this.mutationMessage = "Eligibility updated.";
      } catch (error: unknown) {
        this.mutationMessage = messageForError(error, "Eligibility could not be updated.");
      }
    },
    async bulkUpdateEligibility(
      eligible: boolean,
      reason: string,
      merchantNote?: string,
      productIds?: string[],
    ): Promise<void> {
      const reset = useEligibilityResetStore();
      const repair = useCatalogRepairStore();
      const monitoring = useProductBulkMonitoringStore();
      const checks = useProductMappingChecksStore();
      const policy = useProductBulkPolicyStore();
      if (
        this.bulkSubmitting ||
        reset.submitting ||
        policy.submitting ||
        repair.submitting ||
        checks.submitting ||
        monitoring.submitting
      )
        return;
      if (
        this.bulkNeedsRefresh ||
        reset.needsRefresh ||
        policy.needsRefresh ||
        repair.needsRefresh ||
        checks.needsRefresh ||
        monitoring.needsRefresh
      ) {
        this.listError = "Refresh products before another batch.";
        return;
      }
      let updates: BulkEligibilityUpdate[];
      try {
        updates = buildConfirmedEligibilityUpdates(
          this.products,
          productIds ?? this.selectedProducts.map((product) => product.productId),
          { eligible, reason, ...(merchantNote === undefined ? {} : { merchantNote }) },
        );
      } catch (error: unknown) {
        this.listError = messageForError(error, "Review the selected products.");
        return;
      }
      this.bulkSubmitting = true;
      delete this.listError;
      try {
        this.bulkResult = await this.requireService().bulkUpdateEligibility(updates);
        this.bulkResult.updated.forEach((product) => this.applyUpdatedProduct(product));
        this.mutationMessage = `${this.bulkResult.updated.length} products updated; ${this.bulkResult.failed.length} failed.`;
        this.bulkNeedsRefresh = true;
        await this.refreshBulkProducts();
      } catch (error: unknown) {
        this.listError = messageForError(error, "Bulk eligibility update failed.");
        if (
          !(error instanceof MerchantApiError) ||
          ["network", "server", "unknown"].includes(error.kind)
        ) {
          this.bulkNeedsRefresh = true;
          this.bulkResult = unconfirmedEligibilityResult(updates);
        }
      } finally {
        this.bulkSubmitting = false;
      }
    },
    async refreshBulkProducts(): Promise<void> {
      await this.fetchAppliedList();
      this.bulkNeedsRefresh = this.listError !== undefined;
    },
    async startSync(trigger: "MANUAL" | "RETRY"): Promise<void> {
      this.syncingCatalog = true;
      delete this.listError;
      try {
        const result = await this.requireService().startSync(trigger);
        this.activeJob = result.job;
        this.mutationMessage = result.queued
          ? "Catalog sync queued."
          : "Catalog sync already running.";
      } catch (error: unknown) {
        this.listError = messageForError(error, "Catalog sync could not be started.");
      } finally {
        this.syncingCatalog = false;
      }
    },
    async refreshActiveJob(): Promise<void> {
      if (this.activeJob === undefined) return;
      this.activeJob = await this.requireService().loadJob(this.activeJob.jobId);
    },
    applyUpdatedProduct(updated: ProductRow): void {
      this.products = this.products.map((product) =>
        product.productId === updated.productId ? updated : product,
      );
      this.pageView = {
        ...this.pageView,
        rows: this.pageView.rows.map((row) =>
          row.productId === updated.productId ? updatedListRow(row, updated) : row,
        ),
      };
      if (this.detail?.header.productId === updated.productId) {
        this.detail = productDetailWithUpdatedEligibility(this.detail, updated);
      }
      this.drafts[updated.productId] = defaultEligibilityDraft(updated);
    },
    updateBulkActionState(): void {
      const current = this.pageView.actions;
      const {
        bulkActionsDisabledReason: _bulkActionsDisabledReason,
        ...currentWithoutBulkDisabledReason
      } = current;
      const backendDenied =
        current.bulkActionsDisabledReason !== undefined &&
        current.bulkActionsDisabledReason !== "Select products to use bulk actions.";
      const bulkActionsEnabled = this.selectedIds.length > 0 && !backendDenied;
      this.pageView = {
        ...this.pageView,
        actions: {
          ...currentWithoutBulkDisabledReason,
          bulkActionsEnabled,
          ...bulkActionsDisabledReason(
            this.selectedIds.length,
            backendDenied,
            current.bulkActionsDisabledReason,
          ),
        },
      };
    },
    requireService(): ProductsService {
      if (this.service === undefined) {
        throw new Error("Products store was not configured before use.");
      }
      return this.service;
    },
  },
});

export function defaultProductFilters(): ProductFilters {
  return { limit: 25, sort: "UPDATED_DESC", status: "ACTIVE", searchMode: "PREFIX" };
}

export function defaultEligibilityDraft(product: ProductRow): EligibilityDraft {
  return {
    eligible: product.eligible,
    reason:
      product.eligibilityReason ?? (product.eligible ? "MERCHANT_ENABLED" : "MERCHANT_DISABLED"),
    merchantNote: product.eligibilityNote ?? "",
  };
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  return fallback;
}

function withoutCursor(filters: ProductListFiltersViewModel): ProductListFiltersViewModel {
  const { cursor: _cursor, ...rest } = filters;
  return rest;
}

function appendedProductPage(
  previousPage: ProductListPageViewModel,
  nextPage: ProductListPageViewModel,
): ProductListPageViewModel {
  return {
    ...nextPage,
    appliedFilters: withoutCursor(previousPage.appliedFilters),
    draftFilters: previousPage.draftFilters,
    rows: [...previousPage.rows, ...nextPage.rows],
    sectionStates:
      previousPage.rows.length > 0 && nextPage.rows.length === 0 ? [] : nextPage.sectionStates,
  };
}

function bulkActionsDisabledReason(
  selectedCount: number,
  backendDenied: boolean,
  currentReason: string | undefined,
): { bulkActionsDisabledReason: string } | Record<string, never> {
  if (selectedCount === 0) {
    return { bulkActionsDisabledReason: "Select products to use bulk actions." };
  }

  return backendDenied && currentReason !== undefined
    ? { bulkActionsDisabledReason: currentReason }
    : {};
}

function emptyProductListPageViewModel(): ProductListPageViewModel {
  const filters = defaultProductListFilters();
  return {
    appliedFilters: filters,
    draftFilters: filters,
    summaryCards: [
      {
        id: "catalogProducts",
        label: "Catalog products",
        valueText: "0",
        detailText: "Synced from platform",
        helpText:
          "Total products imported from your commerce platform catalog. The detail shows how many of those products are currently active.",
        tone: "neutral",
      },
      {
        id: "eligibleProducts",
        label: "Eligible",
        valueText: "0",
        detailText: "Ready for widget",
        helpText:
          "Products that can use Price Rescue based on current eligibility rules, catalog data, and required product information.",
        tone: "neutral",
      },
      {
        id: "needsAttention",
        label: "Needs attention",
        valueText: "0",
        detailText: "Identifiers, cost or mappings",
        helpText:
          "Products that may need merchant review before Price Rescue can safely use them, such as missing identifiers, missing cost data, or stale competitor mappings.",
        tone: "neutral",
      },
      {
        id: "lastSynced",
        label: "Last synced",
        valueText: "Not synced",
        detailText: "",
        tone: "neutral",
      },
    ],
    sync: {
      label: "Catalog sync not started",
      detailText: "Run catalog sync to load products and inventory from your commerce platform.",
      status: "UNKNOWN",
      tone: "neutral",
    },
    rows: [],
    warnings: [],
    pagination: { nextCursor: null, canGoNext: false, staleCursor: false },
    actions: {
      repairCatalogEnabled: false,
      repairCatalogDisabledReason: "Products are still loading.",
      syncCatalogEnabled: false,
      syncCatalogDisabledReason: "Products are still loading.",
      bulkActionsEnabled: false,
      bulkActionsDisabledReason: "Select products to use bulk actions.",
      reviewWarningsEnabled: false,
    },
    sectionStates: [],
  };
}

function toCompatibilityProductRow(row: ProductListPageViewModel["rows"][number]): ProductRow {
  return {
    productId: row.productId,
    title: row.title,
    status: row.platformStatusLabel as ProductRow["status"],
    handle: row.productId,
    currency: "USD",
    minPriceMinor: 0,
    maxPriceMinor: 0,
    variantCount: 0,
    eligible:
      row.eligibilityLabel.toLowerCase().includes("eligible") &&
      !row.eligibilityLabel.toLowerCase().includes("not"),
    eligibilitySource: "BACKEND",
    monitoringEnabled: row.monitoringLabel !== "Monitoring off",
    mappingCount: row.mapping.mappedCount,
    staleMappingCount: row.mapping.staleCount,
    inventoryHealth: row.inventoryLabel.toLowerCase().includes("stale") ? "STALE" : "FRESH",
    inventoryFresh: !row.inventoryLabel.toLowerCase().includes("stale"),
  };
}

function productRowFromDetail(detail: ProductDetailResponse): ProductRow {
  return {
    productId: detail.header.productId,
    title: detail.header.title,
    status: detail.identity.status,
    ...(detail.identity.vendor === null ? {} : { vendor: detail.identity.vendor }),
    ...(detail.identity.productType === null ? {} : { productType: detail.identity.productType }),
    handle: detail.identity.handle,
    ...(detail.identity.imageUrl === null ? {} : { imageUrl: detail.identity.imageUrl }),
    currency: detail.variants.items[0]?.currency ?? "USD",
    minPriceMinor: minVariantPrice(detail),
    maxPriceMinor: maxVariantPrice(detail),
    variantCount: detail.variants.items.length,
    eligible: detail.identity.eligibility.enabled,
    ...(detail.identity.eligibility.reasonCode === null
      ? {}
      : { eligibilityReason: detail.identity.eligibility.reasonCode }),
    eligibilitySource: detail.identity.eligibility.source,
    ...(detail.identity.eligibility.note === null
      ? {}
      : { eligibilityNote: detail.identity.eligibility.note }),
    monitoringEnabled: false,
    mappingCount: detail.mappings.summary.total,
    staleMappingCount: detail.mappings.summary.stale,
    inventoryHealth: detailInventoryHealth(detail),
    inventoryFresh: detailInventoryHealth(detail) === "FRESH",
    ...(detail.identity.sourceUpdatedAt === null
      ? {}
      : { sourceUpdatedAt: detail.identity.sourceUpdatedAt }),
  };
}

function productDetailWithUpdatedEligibility(
  detail: ProductDetailResponse,
  updated: ProductRow,
): ProductDetailResponse {
  const eligibility = {
    enabled: updated.eligible,
    label: updated.eligible ? "Price Rescue enabled" : "Excluded",
    reasonCode: updated.eligibilityReason ?? null,
    reasonLabel:
      updated.eligibilityReason === undefined ? null : humanizeReason(updated.eligibilityReason),
    source: updated.eligibilitySource,
    note: updated.eligibilityNote ?? null,
  };
  return {
    ...detail,
    header: {
      ...detail.header,
      title: updated.title,
      status: updated.status,
      imageUrl: updated.imageUrl ?? null,
    },
    identity: {
      ...detail.identity,
      status: updated.status,
      eligibility,
      lastUpdatedAt: new Date().toISOString(),
    },
    priceRescue: {
      ...detail.priceRescue,
      enabled: updated.eligible,
    },
    variants: {
      ...detail.variants,
      items: detail.variants.items.map((variant) => ({ ...variant, eligibility })),
    },
  };
}

function detailInventoryHealth(detail: ProductDetailResponse): ProductRow["inventoryHealth"] {
  return detail.variants.items.some((variant) => variant.inventoryHealth === "STALE")
    ? "STALE"
    : "FRESH";
}

function minVariantPrice(detail: ProductDetailResponse): number {
  return detail.variants.items.reduce(
    (minimum, variant) => Math.min(minimum, variant.priceMinor),
    detail.variants.items[0]?.priceMinor ?? 0,
  );
}

function maxVariantPrice(detail: ProductDetailResponse): number {
  return detail.variants.items.reduce(
    (maximum, variant) => Math.max(maximum, variant.priceMinor),
    detail.variants.items[0]?.priceMinor ?? 0,
  );
}

function updatedListRow(
  row: ProductListPageViewModel["rows"][number],
  updated: ProductRow,
): ProductListPageViewModel["rows"][number] {
  const {
    eligibilityNote: _eligibilityNote,
    eligibilityReasonLabel: _eligibilityReasonLabel,
    ...baseRow
  } = row;
  return {
    ...baseRow,
    eligibilityLabel: updated.eligible ? "Eligible" : "Not eligible",
    eligibilityTone: updated.eligible ? "good" : "warning",
    ...(updated.eligibilityReason === undefined || updated.eligible
      ? {}
      : { eligibilityReasonLabel: humanizeReason(updated.eligibilityReason) }),
    ...(updated.eligibilityNote === undefined || updated.eligibilityNote.trim().length === 0
      ? {}
      : { eligibilityNote: updated.eligibilityNote }),
  };
}

function humanizeReason(reason: string): string {
  return reason
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function errorSection(error: unknown, message: string): ProductSectionStateViewModel {
  if (error instanceof MerchantApiError) {
    return {
      id: "table",
      status: error.kind === "access" ? "PERMISSION_DENIED" : "FAILED",
      message,
      requestId: error.requestId,
    };
  }

  return { id: "table", status: "FAILED", message };
}
